import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Database, CircleNotch } from "@phosphor-icons/react";
import { Card, Btn, KPI, Th, Badge, fmtN } from "./camCommon";

export default function CamDashboard() {
  const [d, setD] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const load = () => api.get("/cam/dashboard").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      await api.post("/cam/seed-demo");
      toast.success("Sample data loaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seeding failed");
    } finally { setSeeding(false); }
  };

  if (!d) return <div className="p-4 text-sm text-slate-500" data-testid="cam-dashboard-loading">Loading…</div>;

  const empty = (d.positions || []).length === 0 && d.inventory_rows === 0;

  return (
    <div className="space-y-4" data-testid="cam-dashboard">
      {empty && (
        <Card title="Get started" subtitle="No commodity data yet. Load a realistic sample set — crude, refined products & biofuels with deals, movements and tickets — or start from Setup & Masters."
          testid="cam-empty-state">
          <Btn onClick={seed} disabled={seeding} data-testid="cam-seed-demo-btn">
            {seeding ? <CircleNotch size={14} className="animate-spin inline mr-1.5" /> : <Database size={14} className="inline mr-1.5" />}
            Load sample data
          </Btn>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Inventory Value" value={`$${fmtN(d.inventory_value)}`}
          sub={`${d.inventory_rows} location/commodity positions`} testid="cam-kpi-inventory" />
        <KPI label="Open Deals" value={fmtN(d.open_deals)} sub="Awaiting scheduling" testid="cam-kpi-open" />
        <KPI label="Scheduled Deals" value={fmtN(d.scheduled_deals)} sub="In logistics pipeline" testid="cam-kpi-scheduled" />
        <KPI label="Recon Exceptions" value={fmtN(d.recon.exceptions)}
          sub={`${d.recon.matched} matched · ${d.recon.missing_tickets} missing tickets · ${d.recon.orphan_tickets} orphans`}
          tone={d.recon.exceptions > 0 ? "text-red-600" : "text-[#1F6B4A]"} testid="cam-kpi-exceptions" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Net Positions" subtitle="Buy vs sell exposure per commodity across all active deals" testid="cam-positions-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <Th>Commodity</Th><Th className="text-right">Bought</Th>
                <Th className="text-right">Sold</Th><Th className="text-right">Net</Th><Th>UoM</Th>
              </tr>
            </thead>
            <tbody>
              {(d.positions || []).map((p) => (
                <tr key={p.commodity} className="border-b border-slate-100" data-testid={`cam-position-${p.commodity}`}>
                  <td className="py-2 pr-3 font-semibold text-[#0B1E3F]">{p.commodity}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtN(p.buy)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtN(p.sell)}</td>
                  <td className={`py-2 pr-3 text-right tabular-nums font-semibold ${p.net > 0 ? "text-emerald-700" : p.net < 0 ? "text-red-600" : "text-slate-500"}`}>
                    {p.net > 0 ? "+" : ""}{fmtN(p.net)}
                  </td>
                  <td className="py-2 text-slate-500 text-xs">{p.uom}</td>
                </tr>
              ))}
              {(d.positions || []).length === 0 && (
                <tr><td colSpan={5} className="py-4 text-center text-slate-400 text-xs">No positions</td></tr>
              )}
            </tbody>
          </table>
        </Card>

        <Card title="Recent Movements" subtitle="Latest logistics activity across pipeline, vessel, rail & truck" testid="cam-movements-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <Th>Movement</Th><Th>Commodity</Th><Th>Route</Th>
                <Th className="text-right">Volume</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {(d.recent_movements || []).map((m) => (
                <tr key={m.movement_no} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-mono text-xs">{m.movement_no}</td>
                  <td className="py-2 pr-3 font-semibold text-[#0B1E3F]">{m.commodity}</td>
                  <td className="py-2 pr-3 text-xs text-slate-600">{m.from_location} → {m.to_location}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtN(m.actual_volume || m.nominated_volume)}</td>
                  <td className="py-2"><Badge v={m.status} /></td>
                </tr>
              ))}
              {(d.recent_movements || []).length === 0 && (
                <tr><td colSpan={5} className="py-4 text-center text-slate-400 text-xs">No movements</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
